import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useUser } from "@/hooks/use-user";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Crown, Zap, Users, CreditCard, Briefcase, Shield } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AppLoadingScreen } from "@/components/ui/app-loading-screen";
import { SaaSGamaLogo } from "@/components/ui/saasgama-logo";
import { PLANS, type BillingCycle } from "@shared/plan-config";


interface SeamlessSubscriptionGateProps {
  children: React.ReactNode;
}

interface PublicCoupon {
  id: number;
  code: string;
  description: string | null;
  discount_type: 'percentage' | 'flat' | 'lifetime';
  discount_value: string;
  applicable_plans: string[] | null;
  expiry_date: string | null;
}

export function SeamlessSubscriptionGate({ children }: SeamlessSubscriptionGateProps) {
  const { user, isLoading: userLoading } = useUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');

  // Super admin bypass - only for actual super admins
  const isSuperAdmin = user && ((user as any).isSuperAdmin === true || user.username === 'Administrator');
  
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // Check subscription status
  const { data: subscriptionStatus, isLoading: subscriptionLoading, error: subscriptionError, refetch: refetchSubscription } = useQuery({
    queryKey: ['/api/subscription-status'],
    queryFn: async () => {
      try {
        const response = await apiRequest('/api/subscription-status');
        console.log('💳 Subscription status:', response);
        return response;
      } catch (error: any) {
        // If authentication error, clear user cache and redirect to login
        if (error.message?.includes('401') || error.message?.includes('Authentication required')) {
          console.log('🔒 Authentication error in subscription check, clearing cache');
          queryClient.setQueryData(["/api/user"], null);
          window.location.href = '/login';
          throw error;
        }
        throw error;
      }
    },
    enabled: !!user && !userLoading,
    staleTime: 0, // Always fetch fresh data
    retry: (failureCount, error: any) => {
      // Don't retry auth errors
      if (error.message?.includes('401') || error.message?.includes('Authentication required')) {
        return false;
      }
      return failureCount < 2;
    },
    refetchOnWindowFocus: true, // Refresh when window focus changes
    refetchInterval: 5000, // Poll every 5 seconds
    refetchIntervalInBackground: false
  });

  // Same coupon-driven lifetime deal already surfaced on the public pricing
  // page and the in-app upgrade flow (modern-subscription.tsx) -- shown here
  // too so it's not missed by someone hitting this paywall before ever
  // visiting the pricing page.
  const { data: publicCoupons = [] } = useQuery<PublicCoupon[]>({
    queryKey: ['/api/public-coupons'],
  });
  const lifetimeDeal = publicCoupons.find(
    c => c.discount_type === 'lifetime' && (!c.applicable_plans?.length || c.applicable_plans.includes('single'))
  );

  // Handle payment success from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentSuccess = urlParams.get('payment');
    
    if (sessionId && paymentSuccess === 'success') {
      console.log('🎉 Payment successful, refreshing subscription status');
      setIsProcessingPayment(false);
      
      // Clear URL parameters
      window.history.replaceState({}, '', window.location.pathname);
      
      // Refresh subscription status
      queryClient.invalidateQueries({ queryKey: ['/api/subscription-status'] });
      queryClient.invalidateQueries({ queryKey: ['/api/user'] });
      refetchSubscription();
      
      toast({
        title: "Payment Successful!",
        description: "Your subscription has been activated.",
      });
    }
  }, [queryClient, toast, refetchSubscription]);

  // Force refresh subscription status when window gains focus
  useEffect(() => {
    const handleFocus = () => {
      console.log('🔄 Window focused, refreshing subscription status');
      refetchSubscription();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetchSubscription]);

  // Start free trial
  const startTrialMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('/api/start-trial', {
        method: 'POST'
      });
      return response;
    },
    onSuccess: (data) => {
      toast({
        title: "🎉 Free Trial Started!",
        description: "Welcome to your 14-day free trial. Enjoy full access to SAASGAMA!",
      });
      
      // Refresh subscription status to show trial access
      queryClient.invalidateQueries({ queryKey: ['/api/subscription-status'] });
      queryClient.invalidateQueries({ queryKey: ['/api/user'] });
      refetchSubscription();
    },
    onError: (error: any) => {
      console.error('Trial start error:', error);
      toast({
        title: "Trial Start Failed",
        description: error.message || "Failed to start trial. Please try again.",
        variant: "destructive",
      });
    }
  });

  // Create checkout session
  const createCheckoutMutation = useMutation({
    mutationFn: async ({ plan, billingCycle, couponCode }: { plan: string; billingCycle: BillingCycle; couponCode?: string }) => {
      const response = await apiRequest('/api/create-checkout-session', {
        method: 'POST',
        body: { plan, billingCycle, ...(couponCode ? { couponCode } : {}) }
      });
      return response;
    },
    onSuccess: (data) => {
      if (data.url) {
        setIsProcessingPayment(true);
        toast({
          title: "Redirecting to checkout",
          description: "Taking you to secure payment...",
        });
        
        // Redirect to Stripe checkout
        window.location.href = data.url;
      }
    },
    onError: (error: any) => {
      console.error('Checkout error:', error);
      setIsProcessingPayment(false);
      toast({
        title: "Payment Error",
        description: error.message || "Failed to create checkout session",
        variant: "destructive",
      });
    }
  });

  // Loading states
  if (userLoading || subscriptionLoading || isProcessingPayment || startTrialMutation.isPending) {
    return (
      <AppLoadingScreen 
        message={isProcessingPayment ? "Processing payment..." : startTrialMutation.isPending ? "Starting your free trial..." : "Checking subscription status..."} 
      />
    );
  }

  // Error state
  if (subscriptionError) {
    console.error('Subscription error:', subscriptionError);
    // On error, show subscription page to be safe
  }

  // If user has active subscription, show dashboard
  if (subscriptionStatus?.hasActiveSubscription && !subscriptionStatus?.redirectToSubscription) {
    return <>{children}</>;
  }

// Subscription plans - pricing sourced from shared/plan-config.ts
  const plans = [
    {
      id: 'single',
      name: PLANS.single.name,
      price: billingCycle === 'yearly' ? PLANS.single.yearly.price : PLANS.single.monthly.price,
      description: 'For individual users managing software costs',
      features: PLANS.single.features,
      icon: Users,
      popular: false
    },
    {
      id: 'team',
      name: PLANS.team.name,
      price: billingCycle === 'yearly' ? PLANS.team.yearly.price : PLANS.team.monthly.price,
      description: 'For growing businesses with multiple users',
      features: PLANS.team.features,
      icon: Users,
      popular: true
    },
    {
      id: 'enterprise',
      name: PLANS.enterprise.name,
      price: billingCycle === 'yearly' ? PLANS.enterprise.yearly.price : PLANS.enterprise.monthly.price,
      description: 'For large organizations with complex needs',
      features: PLANS.enterprise.features,
      icon: Shield,
      popular: false
    }
  ];

  const handleStartTrial = () => {
    startTrialMutation.mutate();
  };

  const handlePlanSelect = (planId: string) => {
    setSelectedPlan(planId);
    createCheckoutMutation.mutate({ plan: planId, billingCycle });
  };

  const handleGetLifetime = (deal: PublicCoupon) => {
    setSelectedPlan('lifetime');
    createCheckoutMutation.mutate({ plan: 'single', billingCycle: 'monthly', couponCode: deal.code });
  };

  const handleLogout = async () => {
    try {
      await apiRequest('/api/logout', { method: 'POST' });
      queryClient.invalidateQueries({ queryKey: ['/api/user'] });
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      {/* Professional Background Overlay */}
      <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40" />
      
      {/* Modal Container */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-white rounded-xl shadow-xl border border-gray-200 max-w-6xl w-full max-h-[95vh] overflow-y-auto">
          {/* Modal Header */}
          <div className="sticky top-0 bg-white border-b border-gray-100 z-10 rounded-t-xl">
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <SaaSGamaLogo size="md" variant="light" />
                <div className="h-6 w-px bg-gray-300" />
                <span className="text-sm font-medium text-gray-600">Subscription Required</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleLogout} 
                  className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 px-3 py-1.5 rounded-lg font-medium text-sm transition-colors"
                >
                  Back to Home
                </button>
                <button
                  onClick={handleLogout} 
                  className="text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-1.5 rounded-lg font-medium text-sm transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Modal Content */}
          <div className="px-6 py-8">
            {/* Account Frozen Warning */}
            {subscriptionStatus?.isFrozen && (
              <div className="mb-8 p-5 bg-red-100 border-l-4 border-red-600 rounded-lg shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 p-2 bg-red-200 rounded-full">
                    <Shield className="h-6 w-6 text-red-700" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-xl font-bold text-red-900">
                        ⚠️ Your Account is Frozen
                      </h2>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white bg-red-600 shadow-sm">
                        LOCKED
                      </span>
                    </div>
                    <p className="text-sm text-red-800 mb-3">
                      Your account has been frozen due to a payment failure or an inactive subscription. Please update your payment information or choose a plan below to restore access to your data.
                    </p>
                    <div className="bg-white/60 border border-red-200 rounded-lg p-3">
                      <p className="text-sm font-semibold text-red-900 flex items-center">
                        <span className="mr-2">💡</span>
                        <strong>Instant Access:</strong> Your account will be automatically unfrozen as soon as a successful payment is processed.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Trial Expiration Warning - Only show if trial has actually expired */}
            {subscriptionStatus?.trialExpired && subscriptionStatus?.trialEndDate && !subscriptionStatus?.isFrozen && (
              <div className="mb-8 p-5 bg-red-50 border-l-4 border-red-400 rounded-lg">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 p-2 bg-red-100 rounded-full">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.232 15.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-xl font-bold text-red-900">
                        ⚠️ Your Free Trial Has Expired
                      </h2>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r from-red-600 to-red-700 shadow-sm">
                        EXPIRED
                      </span>
                    </div>
                    <p className="text-sm text-red-800 mb-3">
                      Your trial ended on {new Date(subscriptionStatus.trialEndDate).toLocaleDateString()}. Choose a plan below to continue accessing SAASGAMA.
                    </p>
                    {subscriptionStatus?.dataDeleteDate && (
                      <div className="bg-white/60 border border-red-200 rounded-lg p-3">
                        <p className="text-sm font-semibold text-red-900 flex items-center">
                          <span className="mr-2">🗑️</span>
                          <strong>Critical:</strong> Your account data will be permanently deleted on {new Date(subscriptionStatus.dataDeleteDate).toLocaleDateString()} if you don't upgrade.
                        </p>
                        <p className="text-xs text-red-700 mt-1 ml-6">
                          Secure your data by choosing a plan below.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* New User Welcome Message - Show if no trial expiration data and not frozen */}
            {(!subscriptionStatus?.trialExpired || !subscriptionStatus?.trialEndDate) && !subscriptionStatus?.isFrozen && (
              <div className="mb-8 p-5 bg-blue-50 border-l-4 border-blue-400 rounded-lg">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 p-2 bg-blue-100 rounded-full">
                    <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-blue-900 mb-2">
                      🚀 Welcome to SAASGAMA
                    </h2>
                    <p className="text-sm text-blue-800">
                      Choose your plan to start managing your SaaS portfolio efficiently and gain insights into your software spending.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {subscriptionStatus?.isFrozen ? 'Reactivate Your Account' : 'Choose Your Plan'}
              </h1>
              <p className="text-gray-600">
                {subscriptionStatus?.isFrozen
                  ? 'Select a plan below to restore full access to your data'
                  : 'Select the perfect plan to continue using SAASGAMA'}
              </p>
        </div>

            {/* Free Trial Option - Only for new users, not frozen accounts */}
            {(!subscriptionStatus?.trialExpired && !subscriptionStatus?.isTrialUser && !subscriptionStatus?.isFrozen) && (
              <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl">
                <div className="text-center">
                  <div className="flex items-center justify-center mb-3">
                    <div className="p-3 bg-blue-100 rounded-full mr-3">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-blue-900">Start Your 14-Day Free Trial</h3>
                  </div>
                  <p className="text-blue-800 mb-4 text-sm">
                    Experience SAASGAMA risk-free with full access to all features. No payment required upfront.
                  </p>
                  <button
                    onClick={() => handleStartTrial()}
                    disabled={startTrialMutation.isPending || createCheckoutMutation.isPending}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors shadow-lg disabled:opacity-50"
                  >
                    🚀 Start Free Trial - No Credit Card Required
                  </button>
                  <p className="text-xs text-blue-700 mt-2">
                    Trial includes: Full feature access • Up to 10 users • 50 applications • Priority support
                  </p>
                </div>
              </div>
            )}

            <div className="text-center mb-6">
              <p className="text-gray-500 text-sm">Or choose a paid plan for immediate full access:</p>
            </div>

            {/* Billing cycle toggle */}
            <div className="flex items-center justify-center mb-6">
              <div className="inline-flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    billingCycle === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('yearly')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    billingCycle === 'yearly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  Yearly
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {plans.map((plan) => {
                const Icon = plan.icon;
                return (
                  <Card 
                    key={plan.id}
                    className={`relative transition-all duration-300 hover:shadow-xl border-2 ${
                      plan.popular 
                        ? 'border-blue-300 shadow-blue-100 bg-gradient-to-b from-blue-50 to-white scale-105' 
                        : 'border-gray-200 hover:border-blue-300 hover:shadow-lg'
                    }`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                        <Badge className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
                          <Crown className="w-3 h-3 mr-1" />
                          Most Popular
                        </Badge>
                      </div>
                    )}
                
                    <CardHeader className="text-center pb-4">
                      <div className={`mx-auto mb-4 p-3 rounded-lg w-fit ${
                        plan.popular ? 'bg-primary/10' : 'bg-gray-100'
                      }`}>
                        <Icon className={`w-8 h-8 ${
                          plan.popular ? 'text-primary' : 'text-gray-600'
                        }`} />
                      </div>
                      <CardTitle className="text-2xl font-bold text-gray-900">{plan.name}</CardTitle>
                      <CardDescription className="text-sm text-gray-600">{plan.description}</CardDescription>
                      <div className="mt-4">
                        <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                        <span className="text-gray-500">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                      </div>
                    </CardHeader>
                
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                      <button
                        onClick={() => handlePlanSelect(plan.id)}
                        disabled={createCheckoutMutation.isPending}
                        className={`w-full h-12 font-semibold transition-all duration-200 rounded-lg flex items-center justify-center ${
                          plan.popular
                            ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg'
                            : 'border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground'
                        }`}
                      >
                        {createCheckoutMutation.isPending && selectedPlan === plan.id ? (
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            <span>Processing...</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <CreditCard className="w-4 h-4" />
                            <span>Subscribe Now</span>
                          </div>
                        )}
                      </button>

                      {/* Limited Offer coupon — Single plan only, below CTA, same style as the public pricing page */}
                      {plan.id === 'single' && lifetimeDeal && (
                        <button
                          type="button"
                          onClick={() => handleGetLifetime(lifetimeDeal)}
                          disabled={createCheckoutMutation.isPending}
                          className="w-full mt-3 rounded-lg overflow-hidden text-left"
                          style={{ border: '1.5px solid #D4AF37' }}
                        >
                          <div className="py-1 px-3 text-center" style={{ background: 'linear-gradient(90deg, #a86200 0%, #D4AF37 50%, #a86200 100%)' }}>
                            <span className="text-white font-bold uppercase tracking-widest" style={{ fontSize: '10px' }}>★ Limited Offer ★</span>
                          </div>
                          <div className="relative py-2 px-3 text-center" style={{ background: 'linear-gradient(135deg, #fffdf0 0%, #fef9c3 100%)' }}>
                            <span className="absolute top-1 left-1.5 text-yellow-300" style={{ fontSize: '8px' }}>✦</span>
                            <span className="absolute top-1 right-1.5 text-yellow-300" style={{ fontSize: '8px' }}>✦</span>
                            <div className="text-xs text-gray-700">
                              {createCheckoutMutation.isPending && selectedPlan === 'lifetime' ? (
                                'Redirecting to checkout...'
                              ) : (
                                <>Lifetime access for <span className="font-bold text-gray-900">${parseFloat(lifetimeDeal.discount_value).toFixed(0)}</span> once.</>
                              )}
                            </div>
                            <div className="mt-1.5 inline-block border border-dashed rounded px-2 py-0.5" style={{ borderColor: '#c78007' }}>
                              <span className="font-bold text-gray-700 tracking-wide" style={{ fontSize: '10px' }}>Code: <span className="font-mono">{lifetimeDeal.code}</span></span>
                            </div>
                          </div>
                        </button>
                      )}
                </CardContent>
              </Card>
            );
          })}
        </div>

            {/* Clean Security Footer */}
            <div className="mt-8 pt-6 border-t border-gray-100 text-center">
              <div className="flex items-center justify-center space-x-6 text-sm text-gray-600 mb-3">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span>Secure Payment</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Cancel Anytime</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span>Instant Access</span>
                </div>
              </div>
              <p className="text-gray-500 text-sm">
                Questions? <span className="text-primary hover:text-primary/80 cursor-pointer font-medium">Contact our support team</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}